'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import Modal from '@/components/common/Modal';
import { useGlobal } from '@/contexts/GlobalContext';

interface ModalContextProps {
  isOpen: boolean;
  openModal: (content: ReactNode) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = useGlobal();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [modalComponent, setModalComponent] = useState<React.ReactNode | null>(null);

  const openModal = (component: React.ReactNode) => {
    setModalComponent(component);
    setIsOpen(true);
  };

  const closeModal = () => {
    setModalComponent(null);
    setIsOpen(false);
  };

  useEffect(() => {
    // NOTE: スクロールバーのレイアウトシフト対応
    // NOTE: ドロワーやモーダル要素が表示されていない（重なっていない）場合に初期化する
    if (context.overlapLevel === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable');
    }
  }, [context.overlapLevel]);

  useEffect(() => {
    if (isOpen) {
      context.handleIncrementOverlapLevel();

      // NOTE: スクロールバーのレイアウトシフト対応
      // NOTE: ドロワーやモーダル要素が表示する（重なる）場合にスクロールバー領域を調整する
      // 強制的にスクロールバーを表示させて幅を計算
      document.body.style.overflow = 'scroll';
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden'; // スクロールを無効化
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.documentElement.style.removeProperty('scrollbar-gutter');
    } else {
      context.handleDecrementOverlapLevel();
    }
  }, [isOpen]);

  useEffect(() => {
    closeModal();
  }, [location.pathname]);

  return (
    <ModalContext.Provider value={{ isOpen, openModal, closeModal }}>
      {children}
      <Modal isOpen={isOpen} modalComponent={modalComponent} />
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
